import { forwardRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CalendarClock, FileDown, PhoneCall, Sparkles } from "lucide-react";
import { CONFIG } from "@/config";

const ICONS = { enroll: Sparkles, book: CalendarClock, talk: PhoneCall };
const ease = [0.22, 1, 0.36, 1];

const OptionCard = ({ option, index, onSelect, onBrochure }) => {
  const Icon = ICONS[option.id] || Sparkles;
  const featured = option.featured;
  return (
    <motion.article
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, ease, delay: 0.1 + index * 0.1 }}
      whileHover={{ y: -4 }}
      className={`relative flex flex-col rounded-3xl p-6 sm:p-7 transition-colors duration-300 ${
        featured
          ? "bg-[#141612] border border-[#B6F23C] shadow-[0_0_40px_rgba(182,242,60,0.15)]"
          : "bg-[#141612]/70 border border-[#282C24] hover:border-[#383F32]"
      }`}
      data-testid={`card-${option.id}`}
    >
      {featured && (
        <span className="absolute -top-3 left-6 rounded-full bg-[#B6F23C] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0B0C0A]" data-testid="card-featured-badge">
          Recommended
        </span>
      )}
      <span className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${featured ? "bg-[#B6F23C]/15 text-[#B6F23C]" : "bg-[#D9B25F]/10 text-[#D9B25F]"}`}>
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="font-display mt-5 text-xl sm:text-2xl font-semibold tracking-tight text-[#F4F5F2]">{option.title}</h3>
      <p className="mt-2 flex-1 text-sm sm:text-base leading-relaxed text-[#A3A89E]">{option.description}</p>
      <button
        type="button"
        onClick={() => onSelect(option)}
        className={`${featured ? "btn-primary" : "btn-outline"} mt-6 w-full`}
        data-testid={`btn-${option.id}`}
      >
        {option.buttonText}
        <ArrowUpRight className="h-4 w-4" />
      </button>
      {option.id === "enroll" && (
        <button
          type="button"
          onClick={onBrochure}
          className="btn-outline mt-3 w-full !border-[#D9B25F]/50 !text-[#D9B25F] hover:!bg-[#D9B25F]/10"
          data-testid="btn-download-brochure"
        >
          <FileDown className="h-4 w-4" />
          {CONFIG.brochure.buttonText}
        </button>
      )}
    </motion.article>
  );
};

export const NextSteps = forwardRef(({ onSelect, onBrochure }, ref) => {
  const t = CONFIG.nextSteps;
  return (
    <section ref={ref} id="next-steps" className="scroll-mt-10 pt-16 sm:pt-24 pb-32" data-testid="next-steps-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease }}
      >
        <span className="eyebrow">
          <span className="h-1.5 w-1.5 rounded-full bg-[#D9B25F] shadow-[0_0_12px_#D9B25F]" />
          Next steps
        </span>
        <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-[#F4F5F2]" data-testid="next-steps-title">
          {t.title}
        </h2>
        <p className="mt-3 max-w-2xl text-sm sm:text-base text-[#A3A89E]">{t.subtitle}</p>
      </motion.div>

      <div className="mt-10 grid gap-5 md:grid-cols-3 md:gap-6 pt-3">
        {CONFIG.options.map((o, i) => (
          <OptionCard key={o.id} option={o} index={i} onSelect={onSelect} onBrochure={onBrochure} />
        ))}
      </div>
    </section>
  );
});
